import React, {useEffect, useState} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {Modal, Button, Input, Upload, Empty, Popconfirm} from 'antd';
import {UploadOutlined, PlusOutlined, DeleteOutlined} from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import {toast} from 'react-toastify';
import api from '../Interceptor';
import {getRoute} from '../api/routes';
import CustomModal from '../components/customModal/customModal';
import {CustomInput, CustomSelect} from '../components/ui';
import ModalFooter from '../components/customModal/modalFooter';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8011';

const EditInvoice = () => {
  const location = useLocation();
  const id = location.state?.invoiceId; // Get the ID from state
  const navigate = useNavigate();
  const [currencies, setCurrencies] = useState([]);
  const [oldCurrency, setOldCurrency] = useState('');
  const [number, setNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerCompany, setCustomerCompany] = useState('');
  const [date, setDate] = useState('');
  const [currency, setCurrency] = useState('');
  const [discount, setDiscount] = useState(0);
  const [note, setNote] = useState('');
  const [taxList, setTaxList] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [taxName, setTaxName] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [itemName, setItemName] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemUnitPrice, setItemUnitPrice] = useState(0);
  const [signature, setSignature] = useState(null);
  const [saveLoader, setSaveLoader] = useState(false);
  const [isTaxModalVisible, setIsTaxModalVisible] = useState(false);
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [originalInvoice, setOriginalInvoice] = useState(null);

  const fetchInvoiceDetails = async () => {
    try {
      const endpoint = `${getRoute(
        'users',
        'admin',
        'invoices',
        false
      )}?id=${id}`;

      const response = await api.get(endpoint);
      const data = response.data;
      const invoiceData = {
        id: data._id,
        number: data.number,
        customerName: data.customerName,
        customerCompany: data.customerCompany,
        date: data.date ? data.date.split('T')[0] : '',
        currency: data.currency?._id || '',
        discount: data.discount || 0,
        note: data.note || '',
        taxes: data.taxes || [],
        items: data.items || [],
        signature: data.signature || null,
      };
      setNumber(invoiceData.number);
      setCustomerName(invoiceData.customerName);
      setCustomerCompany(invoiceData.customerCompany);
      setDate(invoiceData.date);
      setCurrency(invoiceData.currency);
      setDiscount(invoiceData.discount);
      setNote(invoiceData.note);
      setTaxList(invoiceData.taxes);
      setItemList(invoiceData.items);
      setSignature(invoiceData.signature);
      setOriginalInvoice(invoiceData);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  const handleAddTax = () => {
    if (!taxName || !taxRate) return;
    setTaxList((prevTaxes) => [
      ...prevTaxes,
      {name: taxName, rate: parseFloat(taxRate)},
    ]);
    setTaxName('');
    setTaxRate('');
    setIsTaxModalVisible(false);
  };

  const handleAddItem = () => {
    if (!itemName || itemQuantity <= 0 || itemUnitPrice <= 0) return;
    setItemList((prevItems) => [
      ...prevItems,
      {
        name: itemName,
        quantity: Number(itemQuantity),
        unitPrice: Number(itemUnitPrice),
        totalPrice: Number(itemQuantity) * Number(itemUnitPrice),
      },
    ]);
    setItemName('');
    setItemQuantity('');
    setItemUnitPrice('');
    setIsItemModalVisible(false);
  };

  const handleDeleteTax = (index) => {
    setTaxList(taxList.filter((_, i) => i !== index));
  };
  const handleDeleteItem = (index) => {
    const updatedItemList = itemList.filter((_, i) => i !== index);
    setItemList(updatedItemList);
  };

  const uploadImage = async (image) => {
    const data = new FormData();
    data.append('file', image);
    data.append('upload_preset', 'ExpenseTracker');
    try {
      const response = await fetch(
        'https://api.cloudinary.com/v1_1/dqvgbgje8/image/upload',
        {
          method: 'PUT',
          body: data,
        }
      );
      const urlData = await response.json();
      return urlData.url;
    } catch (error) {
      console.error(error);
      toast.error('Image upload failed.');
      return null;
    }
  };

  const handleFileChange = async ({file}) => {
    if (file.status === 'done') {
      try {
        const signatureUrl = await uploadImage(file.originFileObj); // Upload the image to Cloudinary
        setSignature(signatureUrl); // Store the Cloudinary URL in state
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Failed to upload signature image.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveLoader(true);
    let signatureUrl = null;
    if (signature) {
      // ✅ Only upload if it's a File, not a URL string
      if (typeof signature !== 'string') {
        signatureUrl = await uploadImage(signature);
        if (!signatureUrl) {
          toast.error('Failed to upload signature. Please try again.');
          return;
        }
      } else {
        signatureUrl = signature; // keep existing URL
      }
    } else {
      signatureUrl = '';
    }

    const updatedInvoice = {
      id,
      number,
      customerName,
      customerCompany,
      date: new Date(date).toISOString(),
      currency: currency,
      discount: discount || undefined,
      note: note || undefined,
      taxes: taxList.map((tax) => ({
        name: tax.name,
        rate: tax.rate,
      })),

      items: itemList.map((item) => ({
        name: item.itemName || item.name,
        quantity: item.itemQuantity || item.quantity,
        unitPrice: item.itemUnitPrice || item.unitPrice,
      })),

      signature: signatureUrl,
    };
    const normalizeInvoice = (invoice) => ({
      id: invoice.id,
      number: invoice.number,
      customerName: invoice.customerName,
      customerCompany: invoice.customerCompany,
      date: new Date(invoice.date).toISOString().split('T')[0],
      currency: invoice.currency,
      discount: invoice.discount || 0,
      note: invoice.note || '',
      taxes: (invoice.taxes || []).map((t) => ({name: t.name, rate: t.rate})),
      items: (invoice.items || []).map((i) => ({
        name: i.name,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
      })),
      signature: invoice.signature || '',
    });

    const hasChanged =
      JSON.stringify(normalizeInvoice(updatedInvoice)) !==
      JSON.stringify(normalizeInvoice(originalInvoice));

    if (!hasChanged) {
      toast.info('No changes detected.');
      setSaveLoader(false);
      return;
    }
    try {
      const endpoint = getRoute('users', 'admin', 'invoices', false);
      await api.put(endpoint, updatedInvoice);
      toast.success('Invoice updated successfully!');
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to update invoice');
    } finally {
      setSaveLoader(false);
    }
  };

  const getAllCurrencies = async () => {
    try {
      const {data: currencyData} = await api.get(
        `${API_BASE_URL}/api/v1/getAllCurrencies`
      );
      setCurrencies(currencyData);
    } catch (error) {
      if (error?.isUnpaid) {
        return;
      }
      toast.error('Oops! Error while fetching Currencies');
    }
  };

  useEffect(() => {
    getAllCurrencies();
  }, []);

  return (
    <div className="rounded-xl bg-white h-[98%] p-4 border-2 border-[#E9EAEB] font-customFont">
      <h1 className="font-bold text-xl mb-4">Edit Invoice</h1>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <CustomInput
              value={customerName}
              label="Customer Name"
              onChange={(e) => setCustomerName(e.target.value)}
              requir="true"
            />
            <CustomInput
              value={customerCompany}
              label="Customer Company"
              onChange={(e) => setCustomerCompany(e.target.value)}
            />

            <CustomInput
              type="date"
              label="Invoice Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div>
            <div className="mb-4">
              <CustomSelect
                label="Currency"
                value={currency}
                onChange={(value) => setCurrency(value)}
                options={currencies.map((currency) => ({
                  label: `${currency.symbol} ${currency.name}`,
                  value: `${currency._id}`,
                }))}
                // required
                requir="true"
              />
            </div>
            <CustomInput
              type="number"
              label="Discount"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
            <div className="">
              <label className="block text-gray-700 mb-2">
                Signature:
                <Upload
                  listType="picture"
                  onChange={handleFileChange}
                  maxCount={1}
                  customRequest={({onSuccess}) => {
                    setTimeout(() => {
                      onSuccess('ok');
                    }, 0);
                  }}
                  className="mt-1 block w-full py-0.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                >
                  <Button
                    icon={<UploadOutlined />}
                    className="mb-1 bg-transparent border-0 hover:!text-themeColor hover:!bg-transparent hover:text-themeColor2 focus:outline-none"
                  >
                    Upload Signature
                  </Button>
                </Upload>
              </label>
              {signature && (
                <div className="mt-2">
                  <img
                    src={signature}
                    alt="Signature"
                    className="w-32 h-20 object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <h2 className="font-bold text-lg mt-6">Items</h2>
        <Button onClick={() => setIsItemModalVisible(true)}>
          Add Item <PlusOutlined />
        </Button>
        <div className="relative mt-4">
          <div className="max-h-[500px] scrollbar-hide">
            <div className="overflow-x-auto overflow-hidden rounded-lg border border-gray-200">
              <div className="min-w-[800px] sm:min-w-full rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[#E9EAEB] text-[#535862] font-semibold border border-gray-200">
                    <tr>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Quantity</th>
                      <th className="px-4 py-3">Unit Price</th>
                      <th className="px-4 py-3">Total Price</th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemList.length > 0 ? (
                      itemList.map((item, index) => (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 transition-all duration-300 text-center border-t border-gray-200"
                        >
                          <td className="px-4 py-3">{item.name}</td>
                          <td className="px-4 py-3">{item.quantity}</td>
                          <td className="px-4 py-3">{item.unitPrice}</td>
                          <td className="px-4 py-3">{item.totalPrice}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 justify-center items-center">
                              <Popconfirm
                                title="Are you sure you want to delete this item?"
                                onConfirm={() => handleDeleteItem(index)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <DeleteOutlined className="text-red-500 cursor-pointer" />
                              </Popconfirm>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="py-10">
                          <Empty description="No Items found" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <h2 className="font-bold text-lg mt-6">Taxes</h2>
        <Button
          onClick={() => setIsTaxModalVisible(true)}
          className="hover:text-themeColor hover:border-themeColor"
        >
          Add Tax <PlusOutlined />
        </Button>
        <div className="relative mt-4">
          <div className="max-h-[500px] scrollbar-hide">
            <div className="overflow-x-auto overflow-hidden rounded-lg border border-gray-200">
              <div className="min-w-[800px] sm:min-w-full rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[#E9EAEB] text-[#535862] font-semibold border border-gray-200">
                    <tr>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Rate</th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxList.length > 0 ? (
                      taxList.map((tax, index) => (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 transition-all duration-300 text-center border-t border-gray-200"
                        >
                          <td className="px-4 py-3">{tax.name}</td>
                          <td className="px-4 py-3">{tax.rate}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 justify-center items-center">
                              <Popconfirm
                                title="Are you sure you want to delete this item?"
                                onConfirm={() => handleDeleteTax(index)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <DeleteOutlined className="text-red-500 cursor-pointer" />
                              </Popconfirm>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="py-10">
                          <Empty description="No Taxes found" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <label className="font-bold text-lg ">Note:</label>
          <TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mb-4"
          />
        </div>

        <div className="flex justify-center mt-5">
          <Button
            type="primary"
            htmlType="submit"
            loading={saveLoader}
            className="py-2 px-6 mb-10 w-40 bg-themeGradient text-white font-semibold rounded-md shadow-sm hover:bg-themeColor2"
          >
            Update Invoice
          </Button>
        </div>
      </form>

      <CustomModal
        modalTitle="Add Item"
        isOpen={isItemModalVisible}
        onClose={() => setIsItemModalVisible(false)}
      >
        <form onSubmit={handleAddItem}>
          <CustomInput
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            label="Item Name"
            requir="true"
          />
          <CustomInput
            type="number"
            value={itemQuantity}
            onChange={(e) => setItemQuantity(e.target.value)}
            label="Quantity"
            requir="true"
          />
          <CustomInput
            label="Unit Price"
            type="number"
            value={itemUnitPrice}
            onChange={(e) => setItemUnitPrice(e.target.value)}
            requir="true"
          />
          <ModalFooter onCancel={() => setIsItemModalVisible(false)} />
        </form>
      </CustomModal>
      <CustomModal
        modalTitle="Add Tax"
        isOpen={isTaxModalVisible}
        onClose={() => setIsTaxModalVisible(false)}
      >
        <form onSubmit={handleAddTax}>
          <CustomInput
            label="Tax Name"
            value={taxName}
            onChange={(e) => setTaxName(e.target.value)}
            requir="true"
          />
          <CustomInput
            label="Tax Rate (%)"
            type="number"
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
            requir="true"
          />
          <ModalFooter onCancel={() => setIsTaxModalVisible(false)} />
        </form>
      </CustomModal>
    </div>
  );
};

export default EditInvoice;
